'use client'

import { Spinner } from '@/components/extension/spinner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Minus, Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { ChangeEvent, KeyboardEvent } from 'react'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'

interface InfoBarActionProps {
  isGiveaway?: boolean
  isStopSale?: boolean
  isDisabled?: boolean
}

export const InfoBarAction = ({
  isGiveaway = false,
  isStopSale,
  isDisabled,
}: InfoBarActionProps) => {
  const {
    quantity,
    handleQuantityChange,
    handleIncrement,
    handleDecrement,
    handleAddToCartClick,
    isPendingAddToCart,
    handleKeyDown,
    isDisableSize,
  } = useProductDetailStore()
  const t = useTranslations('product_detail')

  const { sortedColorOptions, sizeOptions, sizeSelected, product } =
    useProductDetailSafe()

  // Đổi màu vẫn giữ size đang chọn, nên size đó có thể đã hết hàng ở màu mới
  const isSelectedSizeOutOfStock =
    !product?.is_setdo && !!sizeSelected && isDisableSize(sizeSelected)

  if (isGiveaway) {
    return null
  }

  if (product?.coming_soon)
    return <DisabledAddCartButton message={t('title.coming_soon')} />

  if (!sortedColorOptions.length && !sizeOptions.length)
    return <DisabledAddCartButton message={t('title.out_of_stock')} />

  if (isStopSale)
    return <DisabledAddCartButton message={t('title.stop_selling')} />

  if (isSelectedSizeOutOfStock)
    return <DisabledAddCartButton message={t('title.out_of_stock')} />

  return (
    <div className='flex flex-1 gap-3 border-l border-l-neutral-200 pb-4 pl-2 pt-9 2xl:pl-6'>
      <div
        className={cn(
          'flex w-full gap-3',
          isDisabled && 'pointer-events-none opacity-50'
        )}
      >
        <div className='flex h-10 w-fit items-center rounded-full border border-neutral-200 p-4 text-sm text-neutral-900'>
          <button
            className='flex h-6 w-5 items-center justify-center'
            onClick={() => handleDecrement()}
            type='button'
            aria-label={t('title.decrease_quantity')}
            ga-tracking-value='product-detail__info_bar__quantity__decrement'
            ga-tracking-label='product-detail__info_bar__quantity__decrement'
          >
            <Minus size={12} className='text-neutral-900/75' />
          </button>
          <label htmlFor='quantity-input-bar' className='sr-only'>
            {t('title.quantity')}
          </label>
          <input
            id='quantity-input-bar'
            type='text'
            className='w-9 border-none bg-inherit text-center text-sm outline-none'
            value={quantity}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleQuantityChange(e)
            }
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(e)}
            aria-label={t('title.quantity')}
          />
          <button
            className='flex h-6 w-5 items-center justify-center'
            onClick={() => handleIncrement()}
            type='button'
            aria-label={t('title.increase_quantity')}
            ga-tracking-value='product-detail__info_bar__quantity__increment'
            ga-tracking-label='product-detail__info_bar__quantity__increment'
          >
            <Plus size={12} className='text-neutral-900/75' />
          </button>
        </div>
        <Button
          className='flex h-10 w-full items-center gap-2'
          variant={'default'}
          onClick={() => handleAddToCartClick()}
          id='info-bar-add-cart'
          disabled={isPendingAddToCart || isDisabled}
          ga-tracking-value='product-detail__info_bar__add_to_cart'
          ga-tracking-label='product-detail__info_bar__add_to_cart'
        >
          <Image
            className='h-4 w-4'
            src='/icons/product/icon-cart.svg'
            width={16}
            height={16}
            alt='add cart icon'
          />
          <p className='font-criteria text-base font-medium normal-case leading-6'>
            <span className='flex items-center gap-2'>
              {isPendingAddToCart
                ? t('button.adding')
                : t('button.add_to_cart')}
              {isPendingAddToCart && <Spinner className='h-4 w-4 text-white' />}
            </span>
          </p>
        </Button>
      </div>
    </div>
  )
}

interface DisabledAddCartButtonProps {
  message: string
}

const DisabledAddCartButton = ({ message }: DisabledAddCartButtonProps) => {
  return (
    <div className='flex flex-1 items-center justify-center gap-3 border-l border-l-neutral-200 pb-4 pl-2 2xl:pl-6'>
      <Button
        className='flex h-10 w-fit items-center gap-2'
        variant={'default'}
        disabled
        id='info-bar-add-cart'
        ga-tracking-value='product-detail__info_bar__add_to_cart_disabled'
        ga-tracking-label='product-detail__info_bar__add_to_cart_disabled'
      >
        <Image
          className='h-4 w-4'
          src='/icons/product/icon-cart.svg'
          width={16}
          height={16}
          alt='add cart icon'
        />
        <p className='font-criteria text-base font-medium normal-case leading-6'>
          <span className='flex items-center gap-2'>{message}</span>
        </p>
      </Button>
    </div>
  )
}
